import { businessMs } from './utils';

// ── Build pauses ────────────────────────────────────────────────────────────
//
// A pause stops the clock on one unit, or on the whole order when unit_number
// is null (see 20261105120000_assembly_order_pauses.sql). The order duration
// (lead time) keeps paused time; the assembly duration drops every pause that
// was not rejected. Pending pauses are dropped provisionally - an order cannot
// be posted until each one is approved or rejected, so posted figures only
// ever rest on approved ones.

export const PAUSE_REASONS = {
  missing_parts: 'Missing parts',
  wrong_part: 'Wrong part received',
  faulty_part: 'Faulty / DOA part',
  waiting_on_customer: 'Waiting on customer or sales',
  other: 'Other',
} as const;

export type PauseReason = keyof typeof PAUSE_REASONS;
export type PauseReviewStatus = 'pending' | 'approved' | 'rejected';

export interface PauseRow {
  id: string;
  assembly_order_id: string;
  /** null = every unit of the order. */
  unit_number: number | null;
  reason: PauseReason;
  note: string | null;
  paused_at: string;
  paused_by: string | null;
  resumed_at: string | null;
  resumed_by: string | null;
  review_status: PauseReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  paused_by_tech?: { name: string | null; alias: string | null } | null;
  reviewed_by_tech?: { name: string | null; alias: string | null } | null;
}

/** A half-open span of wall-clock time, in epoch ms. */
export interface Interval { start: number; end: number }

export function pauseReasonLabel(reason: string): string {
  return (PAUSE_REASONS as Record<string, string>)[reason] ?? reason;
}

/** "Unit 2" or "Whole order". */
export function pauseScopeLabel(p: Pick<PauseRow, 'unit_number'>): string {
  return p.unit_number == null ? 'Whole order' : `Unit ${p.unit_number}`;
}

const PAUSABLE = new Set(['building', 'software', 'testing']);

/** Whether an order in this status can be paused (the database enforces the same). */
export function canPauseOrder(status: string | null | undefined): boolean {
  return !!status && PAUSABLE.has(status);
}

export const isOpenPause = (p: Pick<PauseRow, 'resumed_at'>) => !p.resumed_at;

/** Pauses that take time off the assembly duration: all but rejected ones. */
export function countedPauses<T extends Pick<PauseRow, 'review_status'>>(pauses: ReadonlyArray<T>): T[] {
  return pauses.filter(p => p.review_status !== 'rejected');
}

/** True when this pause stops `unit` (1-based): its own, or a whole-order one. */
export function pauseAppliesToUnit(p: Pick<PauseRow, 'unit_number'>, unit: number): boolean {
  return p.unit_number == null || p.unit_number === unit;
}

/** The open pause stopping `unit`, if any - a whole-order pause first. */
export function openPauseForUnit<T extends Pick<PauseRow, 'unit_number' | 'resumed_at'>>(
  pauses: ReadonlyArray<T>, unit: number,
): T | undefined {
  const open = pauses.filter(isOpenPause);
  return open.find(p => p.unit_number == null) ?? open.find(p => p.unit_number === unit);
}

/** Sort and merge overlapping or touching intervals. */
export function mergeIntervals(intervals: ReadonlyArray<Interval>): Interval[] {
  const sorted = intervals.filter(i => i.end > i.start).sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const i of sorted) {
    const last = out[out.length - 1];
    if (last && i.start <= last.end) last.end = Math.max(last.end, i.end);
    else out.push({ ...i });
  }
  return out;
}

/** Time covered by both of two merged interval lists. */
export function intersectIntervals(a: ReadonlyArray<Interval>, b: ReadonlyArray<Interval>): Interval[] {
  const out: Interval[] = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    const start = Math.max(a[i].start, b[j].start);
    const end = Math.min(a[i].end, b[j].end);
    if (end > start) out.push({ start, end });
    if (a[i].end < b[j].end) i++; else j++;
  }
  return out;
}

/** A pause as an interval; an open one runs to `now`. */
function toInterval(p: Pick<PauseRow, 'paused_at' | 'resumed_at'>, now: number): Interval {
  return { start: new Date(p.paused_at).getTime(), end: p.resumed_at ? new Date(p.resumed_at).getTime() : now };
}

/** When `unit` was stopped: its own pauses and whole-order ones, merged.
 *  Pass pauses already filtered with countedPauses where that matters. */
export function unitPauseIntervals(
  pauses: ReadonlyArray<Pick<PauseRow, 'unit_number' | 'paused_at' | 'resumed_at'>>,
  unit: number,
  now = Date.now(),
): Interval[] {
  return mergeIntervals(pauses.filter(p => pauseAppliesToUnit(p, unit)).map(p => toInterval(p, now)));
}

/**
 * When the order as a whole was stopped: the time every unit was paused at
 * once. While one unit waits for a part and another is being built, the order
 * is still being worked on, so that time is not taken off the order.
 */
export function orderPauseIntervals(
  pauses: ReadonlyArray<Pick<PauseRow, 'unit_number' | 'paused_at' | 'resumed_at'>>,
  unitCount: number,
  now = Date.now(),
): Interval[] {
  const n = Math.max(unitCount || 1, 1);
  let acc = unitPauseIntervals(pauses, 1, now);
  for (let u = 2; u <= n && acc.length; u++) acc = intersectIntervals(acc, unitPauseIntervals(pauses, u, now));
  return acc;
}

/**
 * Business time inside [start, end] covered by `intervals`. Measured the way
 * build durations are (businessMs with the lunch hour left in), so it can be
 * subtracted from one directly. Expects merged intervals.
 */
export function pausedBusinessMs(
  start: Date | string,
  end: Date | string,
  intervals: ReadonlyArray<Interval> | undefined,
  activeDays?: Set<string>,
): number {
  if (!intervals?.length) return 0;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  let total = 0;
  for (const i of intervals) {
    const a = Math.max(s, i.start);
    const b = Math.min(e, i.end);
    if (b > a) total += businessMs(new Date(a), new Date(b), activeDays, true);
  }
  return total;
}

/** Business time a single pause has run so far (lunch left in). */
export function pauseBusinessMs(p: Pick<PauseRow, 'paused_at' | 'resumed_at'>, activeDays?: Set<string>, now = Date.now()): number {
  const i = toInterval(p, now);
  return businessMs(new Date(i.start), new Date(i.end), activeDays, true);
}
